import React, { useState } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Reveal from "@/components/Reveal";
import { BookButton, OrderButton } from "@/components/ActionButtons";
import { MENU, CATEGORIES } from "@/data/menu";
import { IMG } from "@/data/restaurant";

const TAG_COLORS = {
  "Signature": "bg-[var(--infuse-pomegranate)] text-white",
  "Chef's pick": "bg-[var(--infuse-gold)] text-[var(--infuse-emerald-dark)]",
  "Vegetarian": "border border-emerald-700 text-emerald-800",
  "Vegan": "bg-emerald-700 text-white",
  "Tandoor": "border border-amber-600 text-amber-800",
  "Spicy": "bg-red-100 text-red-700",
  "Pescatarian": "border border-blue-700 text-blue-800",
  "Indulgent": "border border-amber-600 text-amber-800",
  "Mocktail": "border border-fuchsia-700 text-fuchsia-800",
};

function Tag({ label }) {
  const cls = TAG_COLORS[label] || "border border-gray-400 text-gray-700";
  return (
    <span className={`text-[0.6rem] font-eyebrow px-2 py-0.5 rounded-sm ${cls}`}>{label}</span>
  );
}

export default function Menu() {
  const [active, setActive] = useState(CATEGORIES[0]);

  return (
    <div data-testid="menu-page" className="pt-32 pb-24 bg-[var(--infuse-cream)] min-h-screen">
      {/* Header */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal>
          <span className="gold-divider font-eyebrow text-[var(--infuse-gold)]">The Menu</span>
          <h1 className="font-display text-6xl md:text-7xl text-[var(--infuse-emerald)] mt-5 leading-[0.95]">
            A modern infusion of <em className="not-italic text-[var(--infuse-pomegranate)]">old</em> &amp; new.
          </h1>
          <p className="mt-6 text-[var(--infuse-ink)]/70 text-lg max-w-2xl leading-relaxed">
            Half our menu is meticulously crafted vegetarian and vegan. Every plate is flavour-forward, modern, and unapologetically rooted in heritage.
          </p>
        </Reveal>
      </section>

      {/* Tabs */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-14">
        <Tabs value={active} onValueChange={setActive} data-testid="menu-tabs">
          <TabsList className="flex flex-wrap h-auto bg-transparent gap-1 border-b border-[var(--infuse-gold)]/30 rounded-none p-0 justify-start">
            {CATEGORIES.map((c) => (
              <TabsTrigger
                key={c}
                value={c}
                data-testid={`menu-tab-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="tab-active-underline rounded-none bg-transparent data-[state=active]:bg-transparent data-[state=active]:shadow-none font-eyebrow text-[0.7rem] px-4 py-4 text-[var(--infuse-ink)]/60"
              >
                {c}
              </TabsTrigger>
            ))}
          </TabsList>

          {CATEGORIES.map((c) => (
            <TabsContent key={c} value={c} className="mt-12">
              <div className="grid md:grid-cols-2 gap-x-14 gap-y-10">
                {MENU[c].map((dish, i) => (
                  <Reveal key={dish.name} delay={i * 50}>
                    <article data-testid={`dish-${dish.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="group">
                      <div className="flex items-baseline justify-between gap-4">
                        <h3 className="font-display text-2xl md:text-3xl text-[var(--infuse-emerald)] leading-tight">
                          {dish.name}
                        </h3>
                        <span className="font-eyebrow text-[var(--infuse-pomegranate)] whitespace-nowrap">{dish.price}</span>
                      </div>
                      <div className="my-3 border-b border-dashed border-[var(--infuse-gold)]/40" />
                      <p className="text-[var(--infuse-ink)]/70 leading-relaxed">{dish.desc}</p>
                      {dish.tags?.length > 0 && (
                        <div className="mt-3 flex flex-wrap gap-2">
                          {dish.tags.map((t) => <Tag key={t} label={t} />)}
                        </div>
                      )}
                    </article>
                  </Reveal>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </section>

      {/* CTA strip */}
      <section className="mt-24 relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="relative bg-[var(--infuse-emerald)] text-white overflow-hidden">
            <div className="absolute inset-0 opacity-25" style={{ backgroundImage: `url(${IMG.emeraldBg})`, backgroundSize: "cover" }} />
            <div className="relative p-12 md:p-16 grid md:grid-cols-[1fr_auto] gap-8 items-center">
              <div>
                <span className="gold-divider font-eyebrow text-[var(--infuse-gold-soft)]">Reservations &amp; Ordering</span>
                <h2 className="font-display text-4xl md:text-5xl mt-4 leading-[1]">
                  Hungry already? Book your table or order in.
                </h2>
              </div>
              <div className="flex flex-wrap gap-3">
                <BookButton testid="menu-book-cta" />
                <OrderButton testid="menu-order-cta" />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
